/**
 * Contacts — NumFree Address Book
 * Personal, warm design with interaction timeline
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../auth';
import './ContactsPage.css';

// Stable gradient from name — each contact gets a unique warm color
const nameToHue = (name) => {
  let hash = 0;
  for (let i = 0; i < (name || 'U').length; i++) {
    hash = (name || 'U').charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
};

const avatarStyle = (name) => {
  const hue = nameToHue(name);
  return {
    background: `linear-gradient(135deg, hsl(${hue}, 65%, 55%), hsl(${(hue + 30) % 360}, 55%, 45%))`,
  };
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.trim().substring(0, 2).toUpperCase();
};

const displayName = (c) => c.name || c.company || c.email_address || 'No Name';

const formatPhone = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/[^\d]/g, '');
  if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `+1 (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  return phone;
};
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);
// Normalize phones/emails to [{value,type}] whether they came as arrays or single fields.
const asList = (arr, single) => (Array.isArray(arr) && arr.length ? arr : (single ? [{ value: single, type: '' }] : []));
// Items may be {value,type} (CardDAV/storage_mongo shape) OR plain strings (legacy/manual) — tolerate both.
const fieldVal = (x) => (x && typeof x === 'object' ? (x.value || '') : (x || ''));
// Addresses differ by sync path: storage_mongo stores {type,value}; the People-API path stores
// {formatted,street,city,region,postalCode,country}. Render whichever is present.
const addrStr = (a) => {
  if (!a) return '';
  if (typeof a === 'string') return a;
  if (a.value) return a.value;
  if (a.formatted) return a.formatted;
  return [a.street, a.city, [a.region, a.postalCode || a.postal].filter(Boolean).join(' ').trim(), a.country].filter(Boolean).join(', ');
};
// Website differs by sync path: storage_mongo stores scalar `url`; People-API stores `urls:[{value}]`.
const websiteList = (c) => {
  const out = [];
  if (c.url) out.push(c.url);
  (c.urls || []).forEach((u) => { const v = fieldVal(u); if (v) out.push(v); });
  return out;
};

const timeAgo = (date) => {
  const now = new Date();
  const d = new Date(date);
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const formatDuration = (sec) => {
  if (!sec) return '0:00';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const ContactsPage = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDedup, setShowDedup] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const searchRef = useRef(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formCompany, setFormCompany] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formFavorite, setFormFavorite] = useState(false);
  const [formShortcut, setFormShortcut] = useState('');

  // Load contacts
  const loadContacts = useCallback(async () => {
    try {
      const res = await authFetch(`/api/contacts${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ''}`);
      if (res.ok) {
        const data = await res.json();
        setContacts(data);
      }
    } catch (e) {
      console.error('Failed to load contacts:', e);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => loadContacts(), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load timeline when selecting a contact
  const selectContact = useCallback(async (contact) => {
    setSelectedContact(contact);
    setTimelineLoading(true);
    setTimeline([]);
    try {
      const res = await authFetch(`/api/contacts/${contact._id}/timeline`);
      if (res.ok) {
        const data = await res.json();
        setTimeline(data.events || []);
      }
    } catch (e) {
      console.error('Timeline error:', e);
    } finally {
      setTimelineLoading(false);
    }
  }, []);

  // Open editor
  const openEditor = (contact) => {
    if (contact) {
      setEditingContact(contact);
      setFormName(contact.name || '');
      setFormPhone(contact.phone || '');
      setFormEmail(contact.email || '');
      setFormCompany(contact.company || '');
      setFormNotes(contact.notes || '');
      setFormFavorite(contact.favorite || false);
      setFormShortcut(contact.shortcut || '');
    } else {
      setEditingContact(null);
      setFormName('');
      setFormPhone('');
      setFormEmail('');
      setFormCompany('');
      setFormNotes('');
      setFormFavorite(false);
      setFormShortcut('');
    }
    setShowEditor(true);
  };

  // Save contact
  const saveContact = async () => {
    if (!formName.trim() && !formPhone.trim() && !formEmail.trim()) return;
    setSaving(true);
    try {
      const body = {
        name: formName,
        phone: formPhone,
        contactEmail: formEmail,
        company: formCompany,
        notes: formNotes,
        favorite: formFavorite,
        shortcut: formShortcut.toLowerCase().trim()
      };
      const url = editingContact ? `/api/contacts/${editingContact._id}` : '/api/contacts';
      const method = editingContact ? 'PUT' : 'POST';
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const saved = await res.json();
        setShowEditor(false);
        loadContacts();
        if (selectedContact && selectedContact._id === saved._id) {
          setSelectedContact(saved);
        }
      }
    } catch (e) {
      console.error('Save error:', e);
    } finally {
      setSaving(false);
    }
  };

  // Delete contact
  const deleteContact = async (id) => {
    try {
      const res = await authFetch(`/api/contacts/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setDeleteConfirm(null);
        if (selectedContact && selectedContact._id === id) {
          setSelectedContact(null);
        }
        loadContacts();
      }
    } catch (e) {
      console.error('Delete error:', e);
    }
  };

  // Group contacts by first letter
  const grouped = {};
  const favorites = contacts.filter(c => c.favorite);
  const sorted = [...contacts].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  sorted.forEach(c => {
    const letter = (c.name || '?')[0].toUpperCase();
    if (!grouped[letter]) grouped[letter] = [];
    grouped[letter].push(c);
  });
  const letters = Object.keys(grouped).sort();

  // Quick-scroll alphabet
  const scrollToLetter = (letter) => {
    const el = document.getElementById(`contact-group-${letter}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="contacts-page">
      {/* Contact list */}
      <div className={`contacts-list-pane ${selectedContact ? 'has-detail' : ''}`}>
        {/* Search bar */}
        <div className="contacts-search">
          <div className="search-box">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              ref={searchRef}
              type="text"
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => { setSearchQuery(''); searchRef.current?.focus(); }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            )}
          </div>
          <button className="dedup-btn" onClick={() => setShowDedup(true)} title="Find duplicates">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="2" width="8" height="8" rx="1"/><rect x="8" y="14" width="8" height="8" rx="1"/></svg>
          </button>
          <button className="add-contact-btn" onClick={() => openEditor(null)} title="New contact">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
        </div>

        {/* Contact count */}
        <div className="contacts-count">
          {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
        </div>

        {loading ? (
          <div className="contacts-loading">
            <div className="loading-pulse" />
            <div className="loading-pulse" />
            <div className="loading-pulse" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="contacts-empty">
            <div className="empty-icon">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <h3>{searchQuery ? 'No matches' : 'No contacts yet'}</h3>
            <p>{searchQuery ? 'Try a different search' : 'Add your first contact to get started'}</p>
            {!searchQuery && (
              <button className="empty-add-btn" onClick={() => openEditor(null)}>
                Add Contact
              </button>
            )}
          </div>
        ) : (
          <div className="contacts-scroll">
            {/* Favorites section */}
            {favorites.length > 0 && !searchQuery && (
              <div className="contact-group">
                <div className="group-header">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                  Favorites
                </div>
                {favorites.map(contact => (
                  <ContactRow
                    key={contact._id}
                    contact={contact}
                    selected={selectedContact && selectedContact._id === contact._id}
                    onSelect={() => selectContact(contact)}
                  />
                ))}
              </div>
            )}

            {/* Alphabetical groups */}
            {letters.map(letter => (
              <div className="contact-group" key={letter} id={`contact-group-${letter}`}>
                <div className="group-header">{letter}</div>
                {grouped[letter].map(contact => (
                  <ContactRow
                    key={contact._id}
                    contact={contact}
                    selected={selectedContact && selectedContact._id === contact._id}
                    onSelect={() => selectContact(contact)}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Alphabet sidebar */}
        {!searchQuery && letters.length > 3 && (
          <div className="alpha-rail">
            {letters.map(l => (
              <button key={l} className="alpha-letter" onClick={() => scrollToLetter(l)}>{l}</button>
            ))}
          </div>
        )}
      </div>

      {/* Detail pane */}
      {selectedContact && (
        <div className="contact-detail-pane">
          <button className="detail-back" onClick={() => setSelectedContact(null)}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            Contacts
          </button>

          <div className="detail-hero">
            <div className="detail-avatar" style={avatarStyle(displayName(selectedContact))}>
              {getInitials(displayName(selectedContact))}
            </div>
            <h2 className="detail-name">{displayName(selectedContact)}</h2>
            {selectedContact.company && selectedContact.name !== selectedContact.company && <div className="detail-company">{selectedContact.company}</div>}
          </div>

          {/* Quick actions */}
          <div className="detail-actions">
            {selectedContact.phone && (
              <button className="detail-action" onClick={() => navigate('/dialer', { state: { callbackNumber: selectedContact.phone } })}>
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                <span>Call</span>
              </button>
            )}
            {selectedContact.phone && (
              <button className="detail-action" onClick={() => navigate('/sms', { state: { to: selectedContact.phone } })}>
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                <span>Message</span>
              </button>
            )}
            <button className="detail-action" onClick={() => openEditor(selectedContact)}>
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              <span>Edit</span>
            </button>
            <button className="detail-action danger" onClick={() => setDeleteConfirm(selectedContact._id)}>
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
              <span>Delete</span>
            </button>
          </div>

          {/* Contact info cards */}
          <div className="detail-info">
            {(selectedContact.title || selectedContact.jobTitle) && (
              <div className="info-row">
                <div className="info-label">Title</div>
                <div className="info-value">{selectedContact.title || selectedContact.jobTitle}</div>
              </div>
            )}
            {asList(selectedContact.phones, selectedContact.phone).map((p, i) => (
              <div className="info-row" key={'ph' + i}>
                <div className="info-label">{p && p.type ? cap(p.type) : 'Phone'}</div>
                <div className="info-value">{formatPhone(fieldVal(p))}</div>
              </div>
            ))}
            {asList(selectedContact.emails, selectedContact.email_address || selectedContact.email).map((e, i) => (
              <div className="info-row" key={'em' + i}>
                <div className="info-label">{e && e.type ? cap(e.type) : 'Email'}</div>
                <div className="info-value">{fieldVal(e)}</div>
              </div>
            ))}
            {(selectedContact.addresses || []).map((a, i) => addrStr(a) && (
              <div className="info-row" key={'ad' + i}>
                <div className="info-label">{a && a.type ? cap(a.type) : 'Address'}</div>
                <div className="info-value">{addrStr(a)}</div>
              </div>
            ))}
            {selectedContact.birthday && (
              <div className="info-row">
                <div className="info-label">Birthday</div>
                <div className="info-value">{selectedContact.birthday}</div>
              </div>
            )}
            {websiteList(selectedContact).map((w, i) => (
              <div className="info-row" key={'web' + i}>
                <div className="info-label">Website</div>
                <div className="info-value">
                  {/* Only linkify http(s) — a synced vCard URL could be javascript:/data: (stored XSS). */}
                  {/^https?:\/\//i.test(w)
                    ? <a href={w} target="_blank" rel="noopener noreferrer">{w}</a>
                    : w}
                </div>
              </div>
            ))}
            {selectedContact.notes && (
              <div className="info-row">
                <div className="info-label">Notes</div>
                <div className="info-value notes">{selectedContact.notes}</div>
              </div>
            )}
          </div>

          {/* Interaction Timeline */}
          <div className="detail-timeline">
            <h3 className="timeline-title">Activity</h3>
            {timelineLoading ? (
              <div className="timeline-loading">Loading...</div>
            ) : timeline.length === 0 ? (
              <div className="timeline-empty">No interactions yet</div>
            ) : (
              <div className="timeline-list">
                {timeline.map((event, i) => (
                  <div className={`timeline-item ${event.type}`} key={i}>
                    <div className="timeline-dot">
                      {event.type === 'call' ? (
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                      ) : (
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                      )}
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <span className="timeline-type">
                          {event.type === 'call'
                            ? `${event.direction === 'inbound' ? 'Incoming' : 'Outgoing'} call`
                            : `${event.direction === 'inbound' ? 'Received' : 'Sent'} SMS`
                          }
                          {event.type === 'call' && event.status === 'missed' && <span className="badge-missed">Missed</span>}
                          {event.hasRecording && <span className="badge-vm">VM</span>}
                        </span>
                        <span className="timeline-time">{timeAgo(event.date)}</span>
                      </div>
                      {event.type === 'call' && event.duration > 0 && (
                        <div className="timeline-detail">{formatDuration(event.duration)}</div>
                      )}
                      {event.type === 'sms' && event.body && (
                        <div className="timeline-detail sms-preview">{event.body}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Editor slide-in */}
      {showEditor && (
        <div className="editor-overlay" onClick={() => setShowEditor(false)}>
          <div className="editor-panel" onClick={(e) => e.stopPropagation()}>
            <div className="editor-header">
              <h3>{editingContact ? 'Edit Contact' : 'New Contact'}</h3>
              <button className="editor-close" onClick={() => setShowEditor(false)}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div className="editor-avatar" style={avatarStyle(formName || 'New')}>
              {getInitials(formName || 'New')}
            </div>

            <div className="editor-fields">
              <div className="field">
                <label>Name</label>
                <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Full name" autoFocus />
              </div>
              <div className="field">
                <label>Shortcut</label>
                <div style={{display:'flex',gap:6,alignItems:'center'}}>
                  <input type="text" value={formShortcut} onChange={(e) => setFormShortcut(e.target.value.toLowerCase().slice(0,4))} placeholder="mom" maxLength={4} style={{width:80,textTransform:'lowercase',fontWeight:700,letterSpacing:'0.05em'}} />
                  <button type="button" onClick={() => {
                    const nick = (formName || '').trim().toLowerCase();
                    const first = nick.split(/\s+/)[0];
                    setFormShortcut(first.length >= 3 ? first.substring(0,3) : first || 'abc');
                  }} style={{background:'#f0f0f0',border:'1px solid #ddd',borderRadius:6,padding:'6px 10px',fontSize:11,cursor:'pointer',whiteSpace:'nowrap'}}>Generate</button>
                </div>
                <span style={{fontSize:10,color:'#888',marginTop:2}}>Use with /call {formShortcut || '...'} or /txt {formShortcut || '...'}</span>
              </div>
              <div className="field">
                <label>Phone</label>
                <input type="tel" value={formPhone} onChange={(e) => setFormPhone(e.target.value)} placeholder="+1 (555) 123-4567" />
              </div>
              <div className="field">
                <label>Email</label>
                <input type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} placeholder="name@example.com" />
              </div>
              <div className="field">
                <label>Company</label>
                <input type="text" value={formCompany} onChange={(e) => setFormCompany(e.target.value)} placeholder="Company name" />
              </div>
              <div className="field">
                <label>Notes</label>
                <textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} placeholder="Personal notes..." rows={3} />
              </div>
              <label className="field-check">
                <input type="checkbox" checked={formFavorite} onChange={(e) => setFormFavorite(e.target.checked)} />
                <svg viewBox="0 0 24 24" width="16" height="16" fill={formFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                Favorite
              </label>
            </div>

            <div className="editor-actions">
              <button className="btn-cancel" onClick={() => setShowEditor(false)}>Cancel</button>
              <button className="btn-save" onClick={saveContact} disabled={saving}>
                {saving ? 'Saving...' : (editingContact ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="editor-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="delete-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Contact?</h3>
            <p>This will permanently remove this contact and cannot be undone.</p>
            <div className="delete-actions">
              <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn-delete" onClick={() => deleteContact(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Dedup Dialog */}
      {showDedup && <DedupDialog onClose={() => setShowDedup(false)} onMerged={() => { setShowDedup(false); loadContacts(); }} />}
    </div>
  );
};

// Dedup Dialog
const DedupDialog = ({ onClose, onMerged }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    authFetch('/api/contacts/duplicates/find').then(r => r.json()).then(data => {
      setGroups(data.groups || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggleSelect = (uid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const selectAllDupsInGroup = (group) => {
    // Select all except the first (keep the first, mark rest as dupes)
    setSelected(prev => {
      const next = new Set(prev);
      group.contacts.slice(1).forEach(c => next.add(c.uid));
      return next;
    });
  };

  const selectAllDups = () => {
    const next = new Set();
    groups.forEach(g => g.contacts.slice(1).forEach(c => next.add(c.uid)));
    setSelected(next);
  };

  const clearSelection = () => setSelected(new Set());

  const mergeGroup = async (group) => {
    const keepUid = group.contacts[0].uid; // keep the first one
    const uids = group.contacts.map(c => c.uid);
    try {
      const res = await authFetch('/api/contacts/duplicates/merge-group', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uids, keepUid })
      });
      const data = await res.json();
      if (data.success) {
        // Remove merged group from view
        setGroups(prev => prev.filter(g => g.key !== group.key));
      }
    } catch (e) {
      console.error('Merge failed:', e);
    }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const res = await authFetch('/api/contacts/duplicates/merge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deleteUids: [...selected] })
      });
      const data = await res.json();
      if (data.success) {
        onMerged();
      }
    } catch (e) {
      console.error('Dedup failed:', e);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="dedup-overlay" onClick={onClose}>
      <div className="dedup-dialog" onClick={e => e.stopPropagation()}>
        <div className="dedup-header">
          <h3>Duplicate Contacts</h3>
          <button className="btn-close-dedup" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {loading ? (
          <div className="dedup-loading">Scanning contacts...</div>
        ) : groups.length === 0 ? (
          <div className="dedup-empty">No duplicates found!</div>
        ) : (
          <>
            <div className="dedup-actions-bar">
              <span className="dedup-summary">{groups.length} groups, {selected.size} selected for deletion</span>
              <button className="btn-merge-all" onClick={async () => { for (const g of groups) { await mergeGroup(g); } onMerged(); }}>Merge All</button>
              <button className="btn-select-all-dups" onClick={selectAllDups}>Select All Dupes</button>
              {selected.size > 0 && <button className="btn-clear-sel" onClick={clearSelection}>Clear</button>}
              <button className="btn-delete-dups" onClick={deleteSelected} disabled={selected.size === 0 || deleting}>
                {deleting ? 'Deleting...' : `Delete ${selected.size} Selected`}
              </button>
            </div>

            <div className="dedup-groups">
              {groups.map((g, gi) => (
                <div key={gi} className="dedup-group">
                  <div className="dedup-group-header">
                    <span className="dedup-match-type">{g.matchType === 'name' ? 'Same name' : 'Same phone'}</span>
                    <span className="dedup-match-value">{g.matchValue}</span>
                    <button className="btn-merge-group" onClick={() => mergeGroup(g)}>Merge</button>
                    <button className="btn-select-group" onClick={() => selectAllDupsInGroup(g)}>Select dupes</button>
                  </div>
                  {g.contacts.map((c, ci) => (
                    <label key={c.uid} className={`dedup-contact ${selected.has(c.uid) ? 'marked' : ''} ${ci === 0 ? 'keep' : ''}`}>
                      <input type="checkbox" checked={selected.has(c.uid)} onChange={() => toggleSelect(c.uid)} />
                      <div className="dedup-contact-info">
                        <strong>{c.name || 'No name'}</strong>
                        <span>{[c.phone, c.email_address, c.company].filter(Boolean).join(' · ')}</span>
                        <span className="dedup-source">{c.source === 'google' ? 'Google' : c.source || 'Manual'} {ci === 0 ? '(keep)' : ''}</span>
                      </div>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Individual contact row
const ContactRow = ({ contact, selected, onSelect }) => (
  <button className={`contact-row ${selected ? 'selected' : ''}`} onClick={onSelect}>
    <div className="contact-avatar" style={avatarStyle(displayName(contact))}>
      {getInitials(displayName(contact))}
    </div>
    <div className="contact-row-info">
      <div className="contact-row-name">
        {displayName(contact)}
        {contact.shortcut && <span style={{fontSize:9,background:'#e8eaf6',color:'#283593',padding:'1px 5px',borderRadius:4,marginLeft:6,fontWeight:700,letterSpacing:'0.05em',textTransform:'lowercase'}}>{contact.shortcut}</span>}
        {contact.favorite && <svg viewBox="0 0 24 24" width="12" height="12" fill="#f59e0b" className="fav-star"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>}
      </div>
      <div className="contact-row-sub">
        {[contact.phone ? formatPhone(contact.phone) : contact.email_address, contact.company && contact.name ? contact.company : null].filter(Boolean).join(' · ') || ''}
      </div>
    </div>
  </button>
);

export default ContactsPage;
